import React, { useState, useEffect, useCallback } from 'react';
import { getCajasPaginadas, deleteCaja } from '../services/cajaService';
import { getSucursales } from '../services/sucursalService';
import { Link, useNavigate } from 'react-router-dom';
import { PlusCircle, Edit, Trash2, ArrowLeft, RefreshCw, AlertCircle, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Monitor } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

export default function CajasList() {
    const [cajas, setCajas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Pagination & Search States
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [sucursalId, setSucursalId] = useState('');
    const [sucursales, setSucursales] = useState([]);

    const navigate = useNavigate();
    const { token } = useAuth();

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchTerm), 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const cargarCajas = useCallback(async () => {
        try {
            setLoading(true);
            const data = await getCajasPaginadas(token, page, pageSize, debouncedSearch, sucursalId);
            setCajas(data.content);
            setTotalPages(data.totalPages);
            setTotalElements(data.totalElements);
            setError(null);
        } catch (error) {
            console.error('Error al cargar cajas:', error);
            setError('No se pudieron cargar las ventanillas.');
            toast.error('Error al cargar las cajas');
        } finally {
            setLoading(false);
        }
    }, [token, page, pageSize, debouncedSearch]);

    useEffect(() => {
        cargarCajas();
    }, [cargarCajas]);

    useEffect(() => {
        setPage(0);
    }, [debouncedSearch, pageSize, sucursalId]);

    useEffect(() => {
        const fetchSucursales = async () => {
            try {
                const results = await getSucursales(token);
                setSucursales(results);
            } catch (err) {
                console.error('Error fetching sucursales:', err);
            }
        };
        fetchSucursales();
    }, [token]);

    const handleEliminar = async (id) => {
        if (window.confirm('¿Estás seguro de que deseas eliminar esta ventanilla/caja?')) {
            try {
                await deleteCaja(token, id);
                toast.success('Caja eliminada exitosamente');
                cargarCajas();
            } catch (error) {
                console.error('Error al eliminar:', error);
                toast.error(error.message || 'Error al eliminar la caja');
            }
        }
    };

    const startItem = totalElements === 0 ? 0 : page * pageSize + 1;
    const endItem = Math.min((page + 1) * pageSize, totalElements);

    return (
        <div className="max-w-6xl mx-auto bg-white shadow-sm border border-gray-200 mb-6 font-sans">
            <Toaster position="top-right" />

            {/* Header */}
            <div className="bg-slate-700 p-3 flex justify-between items-center text-white border-b border-slate-600">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/parametrizacion')}
                        className="p-1 hover:bg-slate-600 rounded transition-colors text-slate-300 hover:text-white"
                        title="Parametrización"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="flex items-center gap-2">
                        <Monitor className="w-5 h-5 text-emerald-400" />
                        <h1 className="text-lg font-bold tracking-tight uppercase">Configuración de Cajas y Terminales</h1>
                    </div>
                </div>

                <Link
                    to="/parametrizacion/cajas/nuevo"
                    className="flex items-center gap-1 transition-all rounded-sm text-[0.85rem] font-bold px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"
                >
                    <PlusCircle className="w-4 h-4" />
                    NUEVA CAJA
                </Link>
            </div>

            {/* Controls */}
            <div className="p-3 border-b border-gray-200 flex flex-wrap justify-between items-center gap-4 bg-gray-50">
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                        <label className="text-[0.65rem] font-black text-slate-500 uppercase tracking-widest">Filas:</label>
                        <select
                            value={pageSize}
                            onChange={(e) => setPageSize(Number(e.target.value))}
                            className="border border-slate-300 p-1 bg-white text-[0.8rem] rounded-sm focus:outline-none focus:ring-1 focus:ring-emerald-500"
                            disabled={loading}
                        >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                        </select>
                    </div>
                </div>

                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                        <label className="text-[0.65rem] font-black text-slate-500 uppercase tracking-widest leading-none">Sucursal:</label>
                        <select
                            value={sucursalId}
                            onChange={(e) => setSucursalId(e.target.value)}
                            className="border border-slate-300 px-3 py-1.5 bg-white text-[0.8rem] rounded-sm focus:outline-none focus:ring-1 focus:ring-emerald-500 font-medium min-w-[200px]"
                            disabled={loading}
                        >
                            <option value="">TODAS LAS SUCURSALES</option>
                            {sucursales.map(s => (
                                <option key={s.id} value={s.id}>{s.nombre}</option>
                            ))}
                        </select>
                    </div>

                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Buscar por nombre de caja..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="border border-slate-300 pl-8 pr-3 py-1.5 w-72 focus:ring-1 focus:ring-emerald-500 outline-none text-[0.875rem] rounded-sm bg-white font-medium"
                        />
                        <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-2.5" />
                    </div>
                </div>
            </div>

            {/* Content Table Area */}
            <div className="overflow-x-auto relative">
                <div className="min-w-full">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-[#f8fafc] border-b-2 border-slate-200 text-slate-600 text-[0.65rem] font-black uppercase tracking-[0.1em]">
                                <th className="px-4 py-4 border-r border-slate-100 w-16 text-center">ID</th>
                                <th className="px-4 py-4 border-r border-slate-100">Nombre / Identificador</th>
                                <th className="px-4 py-4 border-r border-slate-100">Sucursal</th>
                                <th className="px-4 py-4 border-r border-slate-100 text-center">Tipo</th>
                                <th className="px-4 py-4 border-r border-slate-100 text-right">Saldo Actual</th>
                                <th className="px-4 py-4 border-r border-slate-100 text-center">Estado</th>
                                <th className="px-4 py-4 text-center w-40">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="text-sm">
                            {loading && cajas.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="px-4 py-16 text-center">
                                        <div className="flex flex-col items-center gap-3">
                                            <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
                                            <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">Obteniendo registros de ventanilla...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : cajas.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="px-4 py-16 text-center text-slate-400 uppercase text-[0.65rem] font-black tracking-widest bg-slate-50/50">
                                        {searchTerm ? 'No se encontraron cajas con ese criterio' : 'No hay cajas configuradas en la red'}
                                    </td>
                                </tr>
                            ) : (
                                cajas.map((caja) => (
                                    <tr key={caja.id} className="border-b border-slate-100 hover:bg-emerald-50/30 transition-colors">
                                        <td className="px-4 py-3 border-r border-slate-100 text-center font-mono text-xs text-slate-400">
                                            {caja.id}
                                        </td>
                                        <td className="px-4 py-3 border-r border-slate-100">
                                            <div className="font-bold text-slate-800 uppercase text-xs">{caja.nombreCaja}</div>
                                            <div className="text-[0.6rem] text-slate-400 font-bold tracking-tight">VIRTUAL TERMINAL ID: {caja.id * 1024}</div>
                                        </td>
                                        <td className="px-4 py-3 border-r border-slate-100">
                                            <span className="text-xs font-medium text-slate-600 uppercase">{caja.sucursal?.nombre || 'SIN SUCURSAL'}</span>
                                        </td>
                                        <td className="px-4 py-3 border-r border-slate-100 text-center">
                                            <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-[0.6rem] font-black rounded-sm border border-slate-200 uppercase tracking-tighter">
                                                {caja.tipoCaja}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 border-r border-slate-100 text-right font-mono text-xs font-bold text-slate-700">
                                            {new Intl.NumberFormat('es-PY').format(caja.saldoActual)}
                                        </td>
                                        <td className="px-4 py-3 border-r border-slate-100 text-center">
                                            {caja.estado === 'ABIERTA' ? (
                                                <span className="flex items-center justify-center gap-1.5 text-emerald-600 font-black text-[0.65rem] uppercase tracking-tighter shadow-sm bg-white border border-emerald-100 px-2 py-1 rounded-full">
                                                    <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></div>
                                                    ACTIVA
                                                </span>
                                            ) : (
                                                <span className="text-slate-400 font-black text-[0.65rem] uppercase tracking-tighter bg-slate-100 border border-slate-200 px-2 py-1 rounded-full inline-block">
                                                    CERRADA
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <div className="flex justify-center items-center gap-2">
                                                <Link
                                                    to={`/parametrizacion/cajas/${caja.id}/editar`}
                                                    className="px-2 py-1.5 text-blue-600 hover:bg-white border border-transparent hover:border-blue-100 transition-all font-black text-[0.65rem] rounded-sm tracking-widest uppercase"
                                                >
                                                    EDITAR
                                                </Link>
                                                <button
                                                    onClick={() => handleEliminar(caja.id)}
                                                    className="px-2 py-1.5 text-rose-500 hover:bg-white border border-transparent hover:border-rose-100 transition-all font-black text-[0.65rem] rounded-sm tracking-widest uppercase"
                                                >
                                                    QUITAR
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Paginator Footer */}
            <div className="p-3 bg-white flex justify-between items-center border-t border-slate-200 text-sm">
                <div className="text-[0.65rem] font-black text-slate-400 uppercase tracking-widest">
                    Ventanillas: <span className="text-slate-800">{startItem}-{endItem}</span> de {totalElements}
                </div>

                <div className="flex gap-1">
                    <button
                        onClick={() => setPage(page - 1)}
                        disabled={page === 0 || loading}
                        className="px-4 py-1.5 bg-slate-100 hover:bg-slate-200 disabled:opacity-30 text-slate-600 font-black text-[0.65rem] rounded-sm transition-all border border-slate-200"
                    >
                        PREV
                    </button>
                    <button
                        onClick={() => setPage(page + 1)}
                        disabled={page >= totalPages - 1 || loading}
                        className="px-4 py-1.5 bg-slate-800 hover:bg-slate-900 disabled:opacity-30 text-white font-black text-[0.65rem] rounded-sm transition-all border border-slate-800"
                    >
                        NEXT
                    </button>
                </div>
            </div>
        </div>
    );
}
